import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '@/typescriptHelpers';
import Checkbox from 'antd/lib/checkbox';
import Select from 'antd/lib/select';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { UpdateFixesFeatureSettingsRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import {
    selectAddFixRequestsToOpenPRsAgainstReleaseBranches,
    selectAppSettings,
    selectThreadFixNotifications,
    selectNotificationIntegration,
    selectPreventFixRequestsAfterSubmission,
    selectPreventFixRequestsAfterSubmissionCustomCopy,
    selectPreventFixRequestsAfterSubmissionCustomCopyEnabled,
} from '@/selectors/selectors';
import { RootState } from 'reducers/reducers';
import {
    AutoCherryPickMatchTokenOn,
    FixesTokenConfig,
    FixesTokenSettings,
    IntegrationType,
    ReleaseTypesForNonBlockingSteps,
} from '@/types';
import { cherryPickPatternValidator } from '@/helpers';
import { Tooltip } from 'antd/lib';
import { FormInstance, useWatch } from 'antd/es/form/Form';
import { cloneDeep, flatMap, fromPairs } from 'lodash';
import { ReleaseTypeTab, ReleaseTypeTabs, groupReleaseTypesByValue } from '../ReleaseTypeTabs';
import { antGrey } from '../styles/globalStyles';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useFixSettingsModal } from './useFixSettingsModal';

const FIXES_TOKEN_CONFIGS_FIELD = 'fixesTokenConfigs';

export const fixesTokenSettingsForReleaseType = (
    config: FixesTokenConfig | undefined,
    releaseType: ReleaseTypesForNonBlockingSteps,
): FixesTokenSettings => {
    const settings = config?.configPerReleaseType?.[releaseType] ?? config;
    return {
        enabled: settings?.enabled ?? false,
        fixRequestTokens: settings?.fixRequestTokens ?? ['[runway:fix-{version}]'],
        matchTokenOn: settings?.matchTokenOn ?? [AutoCherryPickMatchTokenOn.Title],
    };
};

const initialFixesTokenTabs = (config: FixesTokenConfig | undefined) =>
    groupReleaseTypesByValue((releaseType) => fixesTokenSettingsForReleaseType(config, releaseType)).map(
        (group, index) => ({ tab: { id: `tab-${index}`, releaseTypes: group.releaseTypes }, settings: group.value }),
    );

const FixesTokenFields: React.FC<{ tabId: string; form: FormInstance }> = ({ tabId, form }) => {
    const isEnabled = useWatch([FIXES_TOKEN_CONFIGS_FIELD, tabId, 'enabled'], form);

    return (
        <>
            <Form.Item
                name={[FIXES_TOKEN_CONFIGS_FIELD, tabId, 'enabled']}
                valuePropName="checked"
                style={{ marginBottom: 4 }}
            >
                <Checkbox>Allow PRs to be flagged as fixes using a token</Checkbox>
            </Form.Item>
            <Form.Item
                name={[FIXES_TOKEN_CONFIGS_FIELD, tabId, 'fixRequestTokens']}
                label="Fix token patterns"
                style={{ marginLeft: 24 }}
                tooltip="Include a string to flag a PR as a fix. Pattern must include {version}, or {versionConcise}, or no tokens at all."
                rules={[
                    { required: isEnabled },
                    {
                        validator: (_, value) => (isEnabled ? cherryPickPatternValidator(value) : Promise.resolve()),
                    },
                ]}
            >
                <Select
                    style={{ width: '100%' }}
                    allowClear
                    mode="tags"
                    notFoundContent={null}
                    placeholder="Select"
                    disabled={!isEnabled}
                />
            </Form.Item>
            <Form.Item
                name={[FIXES_TOKEN_CONFIGS_FIELD, tabId, 'matchTokenOn']}
                label="Match token on..."
                style={{ marginLeft: 24, marginBottom: 0 }}
                rules={[{ required: isEnabled }]}
            >
                <Select
                    style={{ width: '100%' }}
                    allowClear
                    showSearch={false}
                    mode="multiple"
                    placeholder="Select"
                    disabled={!isEnabled}
                    options={[
                        { value: AutoCherryPickMatchTokenOn.Title, label: 'PR title' },
                        { value: AutoCherryPickMatchTokenOn.Label, label: 'PR label' },
                    ]}
                />
            </Form.Item>
        </>
    );
};

interface EditOpenPRFixesModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditOpenPRFixesModal: React.FC<EditOpenPRFixesModalProps> = (props: EditOpenPRFixesModalProps) => {
    const { form, isLoading, afterClose } = useFixSettingsModal({ open: props.open, onCancel: props.onCancel });
    const dispatch = useDispatch();

    const addFixRequestsToOpenPRsAgainstReleaseBranches = useSelector(
        selectAddFixRequestsToOpenPRsAgainstReleaseBranches(props.appId),
    );
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, props.appId));
    const fixesTokenConfigSnapshot = JSON.stringify(appSettings.fixesTokenConfig ?? null);
    const initialTokenTabs = React.useMemo(
        () => initialFixesTokenTabs(JSON.parse(fixesTokenConfigSnapshot) ?? undefined),
        [fixesTokenConfigSnapshot],
    );
    const [tokenTabs, setTokenTabs] = React.useState<ReleaseTypeTab[]>(() => initialTokenTabs.map((t) => t.tab));
    const [activeTokenTabId, setActiveTokenTabId] = React.useState('tab-0');
    const [tokenTabsKey, setTokenTabsKey] = React.useState(0);

    React.useEffect(() => {
        if (!props.open) return;
        setTokenTabs(initialTokenTabs.map((t) => t.tab));
        setActiveTokenTabId('tab-0');
        setTokenTabsKey((key) => key + 1);
    }, [props.open, initialTokenTabs]);
    const threadFixNotifications = useSelector(selectThreadFixNotifications(props.appId));
    const preventFixRequestsAfterSubmission = useSelector(selectPreventFixRequestsAfterSubmission(props.appId));
    const preventFixRequestsAfterSubmissionCustomCopy = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopy(props.appId),
    );
    const preventFixRequestsAfterSubmissionCustomCopyEnabled = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopyEnabled(props.appId),
    );
    const notificationIntegration = useSelector((state: RootState) =>
        selectNotificationIntegration(state, props.appId),
    );

    // threading is only supported in Slack
    const hasSlackIntegration =
        notificationIntegration != null ? notificationIntegration.id === IntegrationType.Slack : false;

    const isPreventFixRequestsAfterSubmissionEnabled = useWatch(['preventFixRequestsAfterSubmission'], form);
    const isPreventFixRequestsAfterSubmissionCustomCopyEnabled = useWatch(
        ['preventFixRequestsAfterSubmissionCustomCopyEnabled'],
        form,
    );

    const initialValues = {
        addFixRequestsToOpenPRsAgainstReleaseBranches: addFixRequestsToOpenPRsAgainstReleaseBranches,
        [FIXES_TOKEN_CONFIGS_FIELD]: fromPairs(initialTokenTabs.map(({ tab, settings }) => [tab.id, settings])),
        preventFixRequestsAfterSubmission: preventFixRequestsAfterSubmission,
        preventFixRequestsAfterSubmissionCustomCopy: preventFixRequestsAfterSubmissionCustomCopy,
        preventFixRequestsAfterSubmissionCustomCopyEnabled: preventFixRequestsAfterSubmissionCustomCopyEnabled,
        threadFixNotifications: threadFixNotifications,
    };

    const buildFixesTokenConfig = (settingsByTabId: Record<string, FixesTokenSettings>): FixesTokenConfig => {
        const [defaultTab, ...overrideTabs] = tokenTabs;
        return {
            ...settingsByTabId[defaultTab.id],
            configPerReleaseType: fromPairs(
                flatMap(overrideTabs, (tab) =>
                    tab.releaseTypes.map((releaseType) => [releaseType, settingsByTabId[tab.id]]),
                ),
            ),
        };
    };

    const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[] }[] }) => {
        const tokenTabError = errorFields.find(({ name }) => name[0] === FIXES_TOKEN_CONFIGS_FIELD);
        if (tokenTabError) {
            setActiveTokenTabId(String(tokenTabError.name[1]));
        }
    };

    const onFinish = (value) => {
        const request: UpdateFixesFeatureSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            addFixRequestsToOpenPRsAgainstReleaseBranches: value.addFixRequestsToOpenPRsAgainstReleaseBranches,
            fixesTokenConfig: buildFixesTokenConfig(value[FIXES_TOKEN_CONFIGS_FIELD]),
            preventFixRequestsAfterSubmission: value.preventFixRequestsAfterSubmission,
            preventFixRequestsAfterSubmissionCustomCopyEnabled:
                value.preventFixRequestsAfterSubmissionCustomCopyEnabled,
            preventFixRequestsAfterSubmissionCustomCopy: value.preventFixRequestsAfterSubmissionCustomCopy,
            // Only Slack supports threading, so the field is omitted (left untouched) otherwise.
            ...(hasSlackIntegration ? { threadFixNotifications: value.threadFixNotifications } : {}),
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RequiresFixRequestsApprovals, request));
    };

    return (
        <Modal
            title="Fix creation"
            onOk={form.submit}
            okButtonProps={{ loading: isLoading }}
            okText="Save"
            open={props.open}
            onCancel={props.onCancel}
            afterClose={afterClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text style={{ color: antGrey.grey7 }}>
                    Configure when Runway automatically creates fixes from pull requests.
                </Text>
                <Form
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    initialValues={initialValues}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item name="addFixRequestsToOpenPRsAgainstReleaseBranches" valuePropName="checked">
                        <Checkbox>Create a fix for all open PRs against the release branch</Checkbox>
                    </Form.Item>
                    <Form.Item
                        name="preventFixRequestsAfterSubmission"
                        valuePropName="checked"
                        style={{ marginBottom: 4 }}
                    >
                        <Checkbox>Disallow new fix requests once release is submitted</Checkbox>
                    </Form.Item>
                    {isPreventFixRequestsAfterSubmissionEnabled ?
                        <Form.Item
                            name="preventFixRequestsAfterSubmissionCustomCopyEnabled"
                            valuePropName="checked"
                            style={{ marginBottom: 4, marginLeft: 12 }}
                        >
                            <Checkbox>Show a message alongside disallowed action</Checkbox>
                        </Form.Item>
                    :   null}
                    {(
                        isPreventFixRequestsAfterSubmissionCustomCopyEnabled &&
                        isPreventFixRequestsAfterSubmissionEnabled
                    ) ?
                        <Form.Item
                            name="preventFixRequestsAfterSubmissionCustomCopy"
                            style={{ marginLeft: 12 }}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (
                                            isPreventFixRequestsAfterSubmissionCustomCopyEnabled &&
                                            isPreventFixRequestsAfterSubmissionEnabled &&
                                            !value
                                        ) {
                                            return Promise.reject(new Error('Custom copy is required'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input.TextArea rows={5} />
                        </Form.Item>
                    :   null}
                    <Form.Item label="Fix tokens">
                        <ReleaseTypeTabs
                            key={tokenTabsKey}
                            tabs={tokenTabs}
                            onTabsChange={setTokenTabs}
                            activeTabId={activeTokenTabId}
                            onActiveTabChange={setActiveTokenTabId}
                            onTabSplit={(sourceTabId, newTabId) =>
                                form.setFieldValue(
                                    [FIXES_TOKEN_CONFIGS_FIELD, newTabId],
                                    cloneDeep(form.getFieldValue([FIXES_TOKEN_CONFIGS_FIELD, sourceTabId])),
                                )
                            }
                            renderTabContent={(tab) => <FixesTokenFields tabId={tab.id} form={form} />}
                        />
                    </Form.Item>
                    {hasSlackIntegration && (
                        <Form.Item name="threadFixNotifications" valuePropName="checked" style={{ marginBottom: 4 }}>
                            <Checkbox>
                                Thread related fix notifications
                                <Tooltip title="Notifications relevant to a given fix will be threaded in Slack">
                                    <InfoCircleOutlined
                                        style={{ color: antGrey.grey7, marginLeft: 8 }}
                                        onClick={(e) => e.preventDefault()}
                                    />
                                </Tooltip>
                            </Checkbox>
                        </Form.Item>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

export default EditOpenPRFixesModal;
